"use client";

import { Card } from "@/components/ui/card";
import { X, Plus, Check, AlertTriangle, Loader2 } from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";

import {
	formatFileSize,
	getFolderIcon,
	truncateFileName,
} from "@/lib/utils/common.utils";
import { FileUploadStatus } from "@/lib/redux/types/upload.types";
import { RootState, useAppSelector } from "@/lib/redux/root";
import { useUpload } from "@/lib/hooks/useUpload";

interface FileListProps {
	onAddMore: () => void;
}

export function FileList({ onAddMore }: FileListProps) {
	// read aggregated progress from redux and provide fallbacks
	const {
		files,
		inProgess,
		progress: { Total, TotalProgPercent, Completed: stateCompleted } = {},
		totalFileSize,
	} = useAppSelector((state: RootState) => state.upload);

	const { clearUploadFile } = useUpload();

	const totalFiles = Total ?? files.length;
	// console.log(totalFiles, "total files");
	const completedFiles =
		typeof stateCompleted === "number"
			? stateCompleted
			: files.filter((f) => f.status === FileUploadStatus.Completed).length;
	// fallback percent: use TotalProgPercent or compute average of per-file progress
	const overallPercent =
		typeof TotalProgPercent === "number"
			? TotalProgPercent
			: files.length > 0
				? Math.round(
						files.reduce(
							(acc, f) =>
								acc +
								(f.progress ??
									(f.status === FileUploadStatus.Completed ? 100 : 0)),
							0
						) / files.length
					)
				: 0;

	// clamp percent
	const pct = Math.min(Math.max(overallPercent || 0, 0), 100);

	// circular progress geometry
	const size = 48;
	const stroke = 6;
	const radius = (size - stroke) / 2;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (pct / 100) * circumference;

	return (
		<Card className='h-full border-none bg-white py-6'>
			{/* ---------- Header Section ---------- */}

			<div className='mb-4 flex flex-col gap-3 px-6'>
				<div className='flex items-center justify-between'>
					<h2 className='text-xl font-semibold'>
						{files.length} File{files.length !== 1 ? "s" : ""}{" "}
						<span className='text-base font-normal text-gray-500'>
							({formatFileSize(totalFileSize)})
						</span>
					</h2>

					{/* Add more button (hidden during global upload) */}
					{!inProgess && (
						<BrandButton
							onClick={onAddMore}
							size='icon'
							className='h-10 w-10 rounded-full [&_svg]:size-5'
							aria-label='Add more files'
						>
							<Plus className='text-white' />
						</BrandButton>
					)}
				</div>

				{/* Aggregated status row */}
				{inProgess && (
					<div className='flex items-center justify-between gap-4'>
						<div className='text-sm text-gray-600'>
							<span className='font-medium text-gray-800'>
								{completedFiles}
							</span>{" "}
							of <span className='font-medium text-gray-800'>{totalFiles}</span>{" "}
							completed
						</div>

						<div className='flex items-center gap-3'>
							{/* circular overall progress */}
							<div className='relative flex items-center'>
								<svg
									width={size}
									height={size}
									viewBox={`0 0 ${size} ${size}`}
									className='block'
									aria-hidden
								>
									{/* track */}
									<circle
										cx={size / 2}
										cy={size / 2}
										r={radius}
										strokeWidth={stroke}
										stroke='#E5E7EB' /* bg-gray-200 */
										fill='none'
									/>
									{/* progress */}
									<circle
										cx={size / 2}
										cy={size / 2}
										r={radius}
										strokeWidth={stroke}
										stroke='#44ef52ff' /* bg-arc-red */
										fill='none'
										strokeLinecap='round'
										strokeDasharray={`${circumference} ${circumference}`}
										strokeDashoffset={offset}
										style={{ transition: "stroke-dashoffset 300ms linear" }}
										transform={`rotate(-90 ${size / 2} ${size / 2})`}
									/>
								</svg>

								{/* percent label centered */}
								<div className='absolute inset-0 flex items-center justify-center'>
									<span className='text-sm font-medium text-gray-700'>
										{Math.round(pct)}%
									</span>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* ---------- Files List ---------- */}
			<div className='max-h-[600px] space-y-3 overflow-y-auto px-6'>
				{files.map((file) => {
					const progress = Math.min(Math.max(file.progress ?? 0, 0), 100);
					const status = file.status ?? FileUploadStatus.Queued;
					const isUploading = status === FileUploadStatus.Uploading;
					const isCompleted = status === FileUploadStatus.Completed;
					const isFailed = status === FileUploadStatus.Failed;
					const isPending = status === FileUploadStatus.Queued;
					const isUnsupported = status === FileUploadStatus.UnsupportedFileType;

					// choose progress color per-file
					const progressColor =
						isCompleted || isUploading
							? "bg-green-600"
							: isFailed
								? "bg-red-600"
								: "";

					return (
						<div
							key={file.id}
							className='flex flex-col gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50'
						>
							{/* ---------- File Row ---------- */}
							<div className='flex items-start justify-between'>
								{/* Icon + File Name + Size */}
								<div className='flex items-start gap-3'>
									{/* File icon (dynamic) */}
									{typeof getFolderIcon(file.name) === "function"
										? getFolderIcon(file.name)()
										: getFolderIcon(file.name)}

									{/* File info */}
									<div>
										{/* show truncated name with full name on hover */}
										<p
											className='line-clamp-2 text-sm font-medium text-gray-900'
											title={file.name}
											aria-label={file.name}
										>
											{truncateFileName(file.name)}
										</p>
										<div className='flex items-center gap-3'>
											<p className='text-sm text-gray-500'>
												{formatFileSize(file.size)}
											</p>

											{/* small status label */}
											{isUnsupported && (
												<span className='rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800'>
													Unsupported file type
												</span>
											)}
											{isPending && inProgess && (
												<span className='rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800'>
													Pending
												</span>
											)}
											{isUploading && inProgess && (
												<span className='flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800'>
													<Loader2 className='h-3 w-3 animate-spin' />
													Uploading
												</span>
											)}
											{isCompleted && inProgess && (
												<span className='flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs text-green-800'>
													<Check className='h-3 w-3' />
													Uploaded
												</span>
											)}
											{isFailed && inProgess && (
												<span className='flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs text-red-800'>
													<AlertTriangle className='h-3 w-3' />
													Failed
												</span>
											)}
										</div>
									</div>
								</div>

								{/* Actions per-file */}
								<div className='flex items-center gap-2'>
									{/* Remove only allowed before upload starts */}
									{!inProgess && (
										<button
											onClick={() => clearUploadFile(file)}
											className='text-gray-400 transition-colors hover:text-red-600'
											aria-label={`Remove ${file.name}`}
										>
											<X className='h-5 w-5' />
										</button>
									)}
								</div>
							</div>

							{/* ---------- Upload Progress Bar (per-file when uploading or completed/failed) ---------- */}
							{/* Show for uploading, completed (full), failed (show red progress) */}
							{(isUploading || isCompleted || isFailed) && inProgess && (
								<div className='flex flex-col gap-2'>
									<div className='h-1 w-full overflow-hidden rounded-full bg-gray-200'>
										<div
											className={`h-1 rounded-full ${progressColor} transition-all duration-500 ease-out`}
											style={{
												width: `${isCompleted ? 100 : progress}%`,
											}}
										/>
									</div>
									{/* show percentage when uploading */}
									{isUploading && (
										<div className='flex items-center justify-between text-xs text-gray-500'>
											<span>Uploading</span>
											<span>{Math.round(progress)}%</span>
										</div>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</Card>
	);
}
